import React from 'react';

import Icon from 'views/site/Icon.js';

import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * MobileApp
 *
 * The storefront's treatment: phone on the left, content on the right, and on
 * mobile the header rises above the phone so the device is framed by its own
 * heading rather than dropped in cold. Store badges are built rather than
 * imported as artwork — a dark pill with the platform mark and two lines of
 * type — so they take the page's own type and need no image asset.
 *
 * This section does not use the shared Section wrapper, because the two-column
 * grid has to be the section's own container for the mobile reorder to work.
 *
 * Store URLs come from the dealership's own omnishield preferences where they
 * have been set, falling back to the shared listings. Some dealerships publish
 * their own white-labelled build, which is why this is resolved per dealership
 * rather than hard coded.
 */

const MobileApp = ({ tinted = true, utils }) => {

    const mobile = Utils.isMobile();

    const features = [
        'Remote management of every sensor, from anywhere',
        'Live status for the whole network',
        'Text, email and push alerts the instant they fire',
        'Up to 8 emergency contacts',
        'No subscription to receive your alerts'
    ];

    const onStoreClick = platform => {
        let omnishield = utils?.dealership?.omnishield?.get();
        console.log(omnishield?.ios?.app_store_url || 'https://apps.apple.com/gb/app/omnishield/id1491369651');
        if(platform === 'ios') {
            window.open(omnishield?.ios?.app_store_url || 'https://apps.apple.com/gb/app/omnishield/id1491369651');
            return;
        }
        window.open(omnishield?.android?.play_store_url || 'https://play.google.com/store/apps/details?id=com.commlink.omnishield2');
    }

    /*
     * The Apple and Google Play marks, inline.
     *
     * These are trademarks, so they have to be the real shapes rather than the
     * nearest thing in an icon set — the outline glyphs a general-purpose set
     * ships are recognisably wrong. They are also the only two marks on the
     * site that are not ours, which is why they live here rather than in
     * views/site/Icon.js.
     */
    const AppleMark = ({ size = 26 }) => (
        <svg
        aria-hidden={true}
        fill={Theme.colors.white}
        height={size}
        viewBox={'0 0 24 24'}
        width={size}>
            <path d={'M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z'} />
        </svg>
    );

    const PlayMark = ({ size = 26 }) => (
        <svg
        aria-hidden={true}
        fill={Theme.colors.white}
        height={size}
        viewBox={'0 0 24 24'}
        width={size}>
            <path d={'M3.06 1.54a1.5 1.5 0 0 0-.75 1.3v18.32a1.5 1.5 0 0 0 .75 1.3l10.2-10.46L3.06 1.54zm11.4 9.16 2.9-2.97-9.9-5.62 7 8.59zm0 2.6-7 8.59 9.9-5.62-2.9-2.97zm4.72-1.3-2.9-1.65-3.06 3.14 3.06 3.14 2.9-1.65a1.5 1.5 0 0 0 0-2.98z'} />
        </svg>
    );

    const getStoreBadge = (mark, top, bottom, platform) => (
        <div
        onClick={onStoreClick.bind(this, platform)}
        style={{
            alignItems: 'center',
            backgroundColor: Theme.colors.dark(),
            borderRadius: 12,
            cursor: 'pointer',
            display: 'inline-flex',
            gap: 12,
            padding: '12px 22px'
        }}>
            {mark}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                lineHeight: 1.1
            }}>
                <span style={{
                    color: 'rgba(255,255,255,0.7)',
                    fontFamily: Theme.fonts.body,
                    fontSize: 10
                }}>{top}</span>
                <span style={{
                    color: Theme.colors.white,
                    fontFamily: Theme.fonts.body,
                    fontSize: 16,
                    fontWeight: 700
                }}>{bottom}</span>
            </div>
        </div>
    );

    const getHeader = () => (
        <div>
            <span style={{
                ...Theme.textStyles.label(),
                display: 'block',
                marginBottom: 14
            }}>{'The mobile app'}</span>
            <span style={{
                ...Theme.textStyles.sectionHeading({ textAlign: 'left' }),
                display: 'block',
                marginBottom: mobile ? 0 : 16
            }}>{'Your whole network, in your pocket'}</span>
        </div>
    );

    const getBody = () => (
        <div>
            <span style={{
                ...Theme.textStyles.body(),
                display: 'block',
                marginBottom: 28,
                maxWidth: 480
            }}>{'Free on iOS and Android. See every sensor in the house at a glance, get an alert the instant one detects something, and know your home is fine while you are three states away. No subscription, no hidden fees.'}</span>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginBottom: 32
            }}>
                {features.map((feature, index) => (
                    <div
                    key={index}
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 12
                    }}>
                        <Icon name={'check-circle-fill'} size={18} color={Theme.colors.primary()} style={{ flexShrink: 0 }} />
                        <span style={Theme.textStyles.body({ color: Theme.colors.text(), fontWeight: 500 })}>
                            {feature}
                        </span>
                    </div>
                ))}
            </div>

            <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 14
            }}>
                {getStoreBadge(<AppleMark />, 'Download on the', 'App Store', 'ios')}
                {getStoreBadge(<PlayMark />, 'Get it on', 'Google Play', 'android')}
            </div>
        </div>
    );

    const getPhone = () => (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative'
        }}>
            <img
            alt={'The mobile app'}
            src={'images/mobile-app-ios.png'}
            style={{
                height: 'auto',
                maxWidth: mobile ? 280 : 380,
                objectFit: 'contain',
                position: 'relative',
                width: '100%'
            }} />
        </div>
    );

    return (
        <section
        id={'app'}
        style={{
            // Home turns this white when the product grid above it is hidden
            backgroundColor: tinted ? Theme.colors.offWhite : Theme.colors.white,
            width: '100%'
        }}>
            <div style={{
                alignItems: 'center',
                display: 'grid',
                gap: mobile ? 32 : 80,
                gridTemplateColumns: mobile ? '1fr' : '1fr 1fr',
                margin: '0 auto',
                maxWidth: Theme.layout.maxWidth,
                padding: Theme.layout.sectionPadding()
            }}>
                {mobile ? (
                    <>
                        {getHeader()}
                        {getPhone()}
                        {getBody()}
                    </>
                ) : (
                    <>
                        {getPhone()}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            {getHeader()}
                            {getBody()}
                        </div>
                    </>
                )}
            </div>
        </section>
    )
}

export default MobileApp;
